import {
  IRIS_DURATION_S,
  IRIS_EASE,
  radiusToFarthestCorner,
  type IrisOrigin,
} from "@/lib/iris-transition";
import { animate } from "motion/mini";

/**
 * THE `index.html` "Carregando..." OVERLAY
 *
 * The only UI that exists before React mounts. It no longer leaves when React
 * mounts on purpose: mounted React draws a skeleton that still lacks storefront
 * color, and swapping the spinner for that is swapping one warm screen for
 * another. The spinner stays until the first screen is painted, then the iris
 * dismisses it — see `useStorefrontColors`.
 *
 * Why this iris is not a view transition: the spinner must keep SPINNING until
 * covered. A view transition freezes the outgoing page as a bitmap. So the
 * effect is inverted with no API in the middle: the skeleton is already painted
 * underneath, and a growing circular hole in the spinner's own mask reveals it.
 * Mask and `@property --iris-radius` live in `globals.css` so the browser can
 * interpolate a length inside a gradient.
 */
const INITIAL_LOADING_ID = "initial-loading";

/** CSS variable the mask in `globals.css` reads. */
const RADIUS_VAR = "--iris-radius";

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Dismisses "Carregando..." with the iris from `origin`.
 *
 * Idempotent: calling again after it is gone is a no-op (React effects double
 * in development). Instantly gone when the user prefers reduced motion.
 */
export function dismissInitialLoading(origin: IrisOrigin): void {
  const overlay = document.getElementById(INITIAL_LOADING_ID);
  if (!overlay) return;

  if (prefersReducedMotion()) {
    overlay.remove();
    return;
  }

  const radius = radiusToFarthestCorner(origin);
  overlay.style.setProperty(
    "--iris-origin",
    `${origin.x}px ${origin.y}px`,
  );

  const animation = animate(
    overlay,
    { [RADIUS_VAR]: ["0px", `${radius}px`] },
    { duration: IRIS_DURATION_S, ease: IRIS_EASE },
  );

  void animation.finished
    .then(() => overlay.remove())
    .catch(() => overlay.remove());
}
